import React, { useCallback, useContext } from 'react';

import { marked } from 'marked';
import { Link, useParams } from 'react-router-dom';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Container } from '../../components/Container';
import { Loader } from '../../components/Loader';
import { MainLayout } from '../../components/MainLayout';
import { SiteMetadata } from '../../components/SiteMetadata';
import { VisibleOn } from '../../components/VisibleOn';
import { VisibleOnVersion } from '../../components/VisibleOnVersion';
import { useStoreData, useStoryblokStrings } from '../../hooks';
import { ApiContext, CoreContext } from '../../providers';
import { formatDate } from '../../services/formatDate';
import { media } from '../../services/media';
import { parseHTML } from '../../services/parseHTML';
import { themeStyles } from '../../services/themeStyles';
import { NotFound } from '../NotFound';
import { ArticleShare } from './ArticleShare';

/**
 * @param {{ html?: string | null }} props
 */

interface ArticleContentProps {
    html: string;
}

const ArticleContent: React.FC<ArticleContentProps> = ({ html }) => {
    return <Content>{parseHTML(marked(html))}</Content>;
};

interface ArticleMatchParams {
    articleSlug: string;
}

export const Article = () => {
    const { settings } = useContext(CoreContext);
    const params = useParams();
    const { Storyblok } = useContext(ApiContext);
    const articles = useStoreData().storyblok.ArticleItems.items;

    const generalStrings = useStoryblokStrings('general-strings');
    const blogStrings = useStoryblokStrings('blog-strings');

    /** TODO: Add types */
    const articleContent =
        articles &&
        articles.find((article: any) => article.slug === params.articleSlug);

    /**
     * List of articles excluding article that user is currently visiting.
     * The exclude is based on params.articleSlug. Curreently we are displaying
     * slice(0, 4) articles but we can easly make a parameter which indicates
     * how many articles to display.
     */
    /** TODO: Add types */
    const moreArticlesToRead = useCallback(
        () =>
            articles
                ? articles
                      .filter(
                          (article: any) => article.slug !== params.articleSlug,
                      )
                      .slice(0, 4)
                : [],
        [articles, params],
    );

    if (!articles) {
        return <Loader centered />;
    }

    if (!articleContent) {
        return <NotFound />;
    }

    if (!generalStrings) {
        return null;
    }

    const date = formatDate(articleContent.first_published_at);

    const { title, author, site_title, site_description } =
        articleContent.content;

    return (
        <Layout
            className="Article"
            {...(settings.languageVersion === 'upscore'
                ? { withTopBubble: true }
                : {})}
            topBubbleContent={
                <div className="Article__bubble-content">
                    {title && <Title>{title}</Title>}

                    {author && (
                        <VisibleOn desktop>
                            <Date>
                                {generalStrings.written_by} {author}, {date}
                            </Date>
                        </VisibleOn>
                    )}
                </div>
            }
        >
            {site_title && site_description && (
                <SiteMetadata
                    title={site_title}
                    description={site_description}
                />
            )}
            <Container sizeGroup="size-1">
                <Wrapper>
                    <VisibleOnVersion versions={['upscore']}>
                        <Sidebar>
                            <ArticleShare
                                url={window.location.href}
                                title={generalStrings.share_article}
                            />
                            <VisibleOn tablet desktop>
                                <div>
                                    {blogStrings &&
                                        blogStrings.blog_read_more && (
                                            <MoreHeading>
                                                {blogStrings.blog_read_more}
                                            </MoreHeading>
                                        )}
                                    <MoreList>
                                        {/* TODO: Add types */}
                                        {moreArticlesToRead().map(
                                            (article: any) => (
                                                <MoreListItem key={article.id}>
                                                    <Link
                                                        to={`/blog/${article.slug}`}
                                                    >
                                                        {article.content.title}
                                                    </Link>
                                                </MoreListItem>
                                            ),
                                        )}
                                    </MoreList>
                                </div>
                            </VisibleOn>
                        </Sidebar>
                    </VisibleOnVersion>

                    {articleContent.content && (
                        <ArticleContent html={articleContent.content.content} />
                    )}
                </Wrapper>
            </Container>
        </Layout>
    );
};

const Layout = styled(MainLayout)``;

const TitleUpscore = themeStyles(
    'upscore',
    css`
        font-weight: normal;
        font-size: 26px;
        text-align: left;
        margin: 0;
        line-height: 31px;

        ${media.min('md')} {
            font-size: 24px;
            line-height: 28px;
        }

        ${media.min('lg')} {
            font-size: 28px;
            line-height: 34px;
            margin-bottom: 20px;
        }

        ${media.min('xl')} {
            font-size: 30px;
            line-height: 36px;
        }

        ${media.min('xxl')} {
            font-size: 3.6vw;
            line-height: 4.3vw;
            margin-bottom: 35px;
        }
    `,
);

const Title = styled.h1`
    ${TitleUpscore}
`;

const DateUpscore = themeStyles(
    'upscore',
    css`
        text-align: left;

        ${media.min('xxl')} {
            font-size: 25px;
            margin-left: 11px;
            font-weight: 300;
        }
    `,
);

const Date = styled.div`
    ${DateUpscore}
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;

    ${media.min('md')} {
        flex-direction: row;
        padding-top: 75px;
        padding-bottom: 75px;
    }

    ${media.min('xxl')} {
        padding-top: 10.8%;
        padding-bottom: 41px;
    }

    img {
        max-width: 100%;
    }
`;

const Sidebar = styled.div`
    order: 1;
    border-bottom: 1px solid #817f80;
    margin: 0 24px 0 24px;
    padding-top: 41px;
    margin-bottom: 20px;
    padding-bottom: 51px;

    ${media.min('md')} {
        order: 0;
        max-width: 240px;
        width: 100%;
        border: 0;
        padding-top: 220px;
    }

    ${media.min('lg')} {
        max-width: 300px;
    }

    ${media.min('xxl')} {
        max-width: 530px;
        margin-left: 132px;
        padding-top: 22.3vw;
    }
`;
const Content = styled.div`
    ${media.min('lg')} {
        padding-left: 15px;
        max-width: 870px
        margin-left: auto;
    }

    ${media.min('xl')} {
        max-width: 60%;
    }

    ${media.min('xxl')} {
        max-width: 51%;
    }
`;

const MoreHeading = styled.h3`
    font-size: 24px;

    ${media.min('xl')} {
        font-size: 26px;
    }

    ${media.min('xxl')} {
        font-size: 36px;
        margin-top: 0;
        margin-bottom: 31px;
    }
`;

const MoreList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
`;

const MoreListItem = styled.li`
    margin-bottom: 15px;

    ${media.min('xl')} {
        margin-bottom: 30px;
    }

    ${media.min('xxl')} {
        margin-bottom: 39px;
    }

    a {
        font-weight: 300;

        ${media.min('xl')} {
            font-size: 18px;
        }

        ${media.min('xxl')} {
            font-size: 26px;
        }
    }
`;
